// src/utils/bookingStatusFormatUtil.js
//
// Formatting for the Track Booking card (components/BookingStatus*.jsx):
// a booking's hours per court, collapsed into ranges, and each date's span.

import { format } from 'date-fns';
import { sortTimeSlots, parseTimeSlotStartHour, formatTimeSlotRange, formatTimeSlotRangeParts, getCourtLabel } from './bookingUtils';

/**
 * Formats a stored booking date ("yyyy-MM-dd", optionally with a time part).
 * Parsed as LOCAL midnight rather than `new Date(dateStr)`, which JS treats
 * as UTC midnight and can roll back a day in some timezones.
 *
 * @param {string} dateStr
 * @param {string} pattern - a date-fns pattern, e.g. 'MMMM dd, yyyy'
 * @returns {string} 'N/A' when missing; the raw string when unparseable
 */
export const formatBookingDate = (dateStr, pattern) => {
  if (!dateStr) return 'N/A';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return format(new Date(y, m - 1, d), pattern);
};

/**
 * Reads a slot's court list in whichever shape it was stored.
 *
 * Firebase hands an array back as an object keyed by index when it has gaps
 * ({0: 3, 2: 4}) — the values are the courts. An older `{3: true}` map keeps
 * the court in the key instead, so all-boolean values mean "read the keys".
 *
 * @param {Array|object|null} courts
 * @returns {number[]}
 */
export const toCourtNumbers = (courts) => {
  if (!courts) return [];
  if (Array.isArray(courts)) return courts.filter(c => c !== null && c !== undefined).map(Number);
  const values = Object.values(courts);
  if (values.length > 0 && values.every(v => typeof v === 'boolean')) {
    return Object.keys(courts).filter(k => courts[k]).map(Number);
  }
  return values.filter(c => c !== null && c !== undefined).map(Number);
};

/**
 * Collapses one-hour slots into ranges for the status card.
 *
 * A lone hour keeps the app's usual "4:00-5:00 PM"; a run of consecutive
 * hours becomes "4:00PM - 7:00PM" so a long booking reads as one span
 * instead of a list. A gap starts a new range, so "4-5PM" and "8-9PM" stay
 * apart rather than implying four unbroken hours.
 *
 * @param {string[]} timeSlots - raw slot keys, e.g. ["4-5PM", "5-6PM"]
 * @returns {string} comma-separated ranges, or '' for none
 */
export const formatSlotRuns = (timeSlots = []) => {
  const sorted = [...new Set(sortTimeSlots(timeSlots).filter(Boolean))];
  const runs = [];
  sorted.forEach((slot) => {
    const run = runs[runs.length - 1];
    const previous = run && run[run.length - 1];
    const consecutive = previous && parseTimeSlotStartHour(previous) + 1 === parseTimeSlotStartHour(slot);
    if (consecutive) run.push(slot);
    else runs.push([slot]);
  });

  // sortTimeSlots puts the after-midnight hours (12AM on) FIRST, so a late
  // booking of 10PM-1AM arrives as [12-1AM] ... [10-11PM, 11-12AM]. When the
  // last run reaches midnight and the first starts at it, they are one span.
  const lastRun = runs[runs.length - 1];
  if (runs.length > 1
    && parseTimeSlotStartHour(runs[0][0]) === 0
    && parseTimeSlotStartHour(lastRun[lastRun.length - 1]) === 23) {
    const afterMidnight = runs.shift();
    runs[runs.length - 1] = [...lastRun, ...afterMidnight];
  }

  return runs.map((run) => {
    if (run.length === 1) return formatTimeSlotRange(run[0]);
    const first = formatTimeSlotRangeParts(run[0]);
    const last = formatTimeSlotRangeParts(run[run.length - 1]);
    return `${first.start}${first.startMeridiem} - ${last.end}${last.endMeridiem}`;
  }).join(', ');
};

/**
 * One line per court for a single date: [{ court, label, times }], courts in
 * number order, e.g. { label: "Court 1", times: "4:00PM - 7:00PM" }.
 *
 * @param {object} config - club config, for court names
 * @param {object} timeSlots - { "4-5PM": [1, 2], ... } for one date
 * @returns {Array<{court: number, label: string, times: string}>}
 */
export const buildCourtScheduleLines = (config, timeSlots) => {
  const slotsByCourt = new Map();
  Object.entries(timeSlots || {}).forEach(([slot, courts]) => {
    toCourtNumbers(courts).forEach((court) => {
      if (!slotsByCourt.has(court)) slotsByCourt.set(court, []);
      slotsByCourt.get(court).push(slot);
    });
  });

  return [...slotsByCourt.keys()]
    .sort((a, b) => a - b)
    .map(court => ({
      court,
      label: getCourtLabel(config, court),
      times: formatSlotRuns(slotsByCourt.get(court)),
    }));
};

/**
 * A date's overall booked span across every court — "8:00PM - 10:00PM" —
 * for the Date & Time box. Uses the same range rules as formatSlotRuns.
 *
 * @param {object} timeSlots - { "8-9PM": [3], ... } for one date
 * @returns {string} '' when the date has no hours
 */
export const formatDateTimeSpan = (timeSlots) => formatSlotRuns(
  Object.entries(timeSlots || {})
    .filter(([, courts]) => toCourtNumbers(courts).length > 0)
    .map(([slot]) => slot)
);

/**
 * A booking's per-date schedule, falling back to its legacy single-date
 * fields when it has no `dates` array.
 *
 * @param {object} booking - a grouped booking (see groupBookingRecords)
 * @returns {Array<{date: string, timeSlots: object}>}
 */
export const getBookingDateGroups = (booking) => (
  Array.isArray(booking?.dates) && booking.dates.length > 0
    ? booking.dates
    : [{ date: booking?.date, timeSlots: booking?.timeSlots }]
);
